/**
 * PollPage — TASK-32 (Attendee facing meeting poll page)
 *
 * Public voting interface for group meeting polls.
 * Attendees can view options in their timezone, see participant vote heatmaps,
 * and submit or update their availability.
 */

import { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@/shared/i18n/useT';
import { visitorTimeZone } from '@/client/booking/datetime';
import { TimezoneSelect } from '@/client/booking/TimezoneSelect';
import { LegalFooter } from '@/shared/legal';

interface PublicVote {
  id: string;
  voterName: string;
  selectedSlots: string[];
  createdAt: string;
}

interface SlotTally {
  slot: string;
  votesCount: number;
  voters: string[];
}

interface PollData {
  id: string;
  title: string;
  description: string | null;
  duration_min: number;
  location_type: string;
  location_value: string | null;
  slots: string[];
  status: 'open' | 'closed' | 'finalized';
  finalized_slot: string | null;
}

export function PollPage() {
  const params = useParams<{ pollId?: string }>();
  const [searchParams] = useSearchParams();
  const pollId = params.pollId || searchParams.get('poll');
  const { formatLocale } = useI18n();

  const [poll, setPoll] = useState<PollData | null>(null);
  const [tallies, setTallies] = useState<SlotTally[]>([]);
  const [totalVoters, setTotalVoters] = useState(0);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Voting form state
  const [voterName, setVoterName] = useState('');
  const [voterEmail, setVoterEmail] = useState('');
  const [selectedSlots, setSelectedSlots] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [voteSuccess, setVoteSuccess] = useState(false);

  // Timezone selection
  const visitorZone = useMemo(() => visitorTimeZone(), []);
  const [timeZone, setTimeZone] = useState(visitorZone);

  const fetchPoll = async () => {
    if (!pollId) {
      setError('Poll ID is required');
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`/api/polls/public/${encodeURIComponent(pollId)}`);
      if (!res.ok) {
        if (res.status === 404) throw new Error('Poll not found');
        throw new Error('Failed to load meeting poll');
      }
      const data = (await res.json()) as {
        poll: PollData;
        votes: PublicVote[];
        tally: { tallies: SlotTally[]; totalVoters: number };
      };
      setPoll(data.poll);
      setTallies(data.tally.tallies);
      setTotalVoters(data.tally.totalVoters);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load poll');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchPoll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollId]);

  const toggleSlot = (slot: string) => {
    if (selectedSlots.includes(slot)) {
      setSelectedSlots(selectedSlots.filter((s) => s !== slot));
    } else {
      setSelectedSlots([...selectedSlots, slot]);
    }
  };

  const handleVoteSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!pollId || selectedSlots.length === 0 || submitting) return;

    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/polls/public/${encodeURIComponent(pollId)}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          voterName: voterName.trim(),
          voterEmail: voterEmail.trim().toLowerCase(),
          selectedSlots,
        }),
      });

      if (!res.ok) {
        const errJson = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(errJson.error || 'Failed to submit vote');
      }

      setVoteSuccess(true);
      await fetchPoll();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to submit vote');
    } finally {
      setSubmitting(false);
    }
  };

  const formatSlotTime = (iso: string) => {
    return new Intl.DateTimeFormat(formatLocale, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone,
    }).format(new Date(iso));
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-xl p-6 text-center text-sm text-muted">
        Loading meeting poll…
      </div>
    );
  }

  if (error && !poll) {
    return (
      <div className="mx-auto max-w-xl p-6">
        <div className="rounded-xl border border-danger bg-danger-soft p-4 text-center">
          <h2 className="text-base font-semibold text-danger">Poll Not Found</h2>
          <p className="mt-1 text-xs text-danger">{error}</p>
        </div>
      </div>
    );
  }

  if (!poll) return null;

  const isFinalized = poll.status === 'finalized';
  const isClosed = poll.status === 'closed';

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 space-y-6">
      <header className="rounded-2xl border border-border bg-surface-2 p-5 sm:p-6 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h1 className="text-xl font-bold text-fg">{poll.title}</h1>
          <span
            className={`rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wider ${
              isFinalized
                ? 'bg-accent text-accent-fg'
                : isClosed
                  ? 'bg-surface-3 text-muted'
                  : 'bg-accent-soft text-accent'
            }`}
          >
            {isFinalized ? 'Finalized' : isClosed ? 'Closed' : 'Voting Open'}
          </span>
        </div>

        {poll.description && (
          <p className="text-sm text-muted">{poll.description}</p>
        )}

        <div className="flex flex-wrap items-center gap-4 text-xs text-muted border-t border-border pt-3">
          <span>⏱ {poll.duration_min} minutes</span>
          <span>📍 {poll.location_type.replace('_', ' ')}</span>
          <span>👥 {totalVoters} {totalVoters === 1 ? 'participant' : 'participants'}</span>
        </div>
      </header>

      <div className="flex items-center justify-between">
        <span className="text-xs font-medium text-muted">Timezone:</span>
        <div className="w-64">
          <TimezoneSelect
            value={timeZone}
            visitorZone={visitorZone}
            professionalZone={visitorZone}
            onChange={setTimeZone}
          />
        </div>
      </div>

      {/* Winner announcement if finalized */}
      {isFinalized && poll.finalized_slot && (
        <div className="rounded-2xl border border-accent bg-accent-soft p-5 text-center space-y-2">
          <span className="text-xs font-bold uppercase tracking-wider text-accent">Selected Meeting Time</span>
          <h2 className="text-lg font-bold text-fg">{formatSlotTime(poll.finalized_slot)}</h2>
          <p className="text-xs text-muted">
            The organizer has finalized this time. Check your calendar or email for confirmed details.
          </p>
        </div>
      )}

      {/* Voting form */}
      {!isFinalized && !isClosed && (
        <form onSubmit={handleVoteSubmit} className="rounded-2xl border border-border bg-surface p-5 sm:p-6 space-y-4">
          <div>
            <h2 className="text-base font-semibold text-fg">Cast Your Vote</h2>
            <p className="text-xs text-muted">Select all time slots that work for you.</p>
          </div>

          {voteSuccess && (
            <div className="rounded-lg border border-success bg-success-soft p-3 text-xs text-fg">
              ✓ Your vote has been recorded! You can update your availability anytime using the same email.
            </div>
          )}

          {error && (
            <div className="rounded-lg border border-danger bg-danger-soft p-3 text-xs text-danger">
              {error}
            </div>
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs text-muted">
              Your Name *
              <input
                type="text"
                required
                value={voterName}
                onChange={(e) => setVoterName(e.target.value)}
                placeholder="Alex Morgan"
                className="tap-target mt-1 w-full rounded-full border border-border-strong bg-surface-2 px-3 text-xs text-fg"
              />
            </label>
            <label className="text-xs text-muted">
              Your Email *
              <input
                type="email"
                required
                value={voterEmail}
                onChange={(e) => setVoterEmail(e.target.value)}
                placeholder="alex@example.com"
                className="tap-target mt-1 w-full rounded-full border border-border-strong bg-surface-2 px-3 text-xs text-fg"
              />
            </label>
          </div>

          <div className="space-y-2 pt-2">
            <label className="block text-xs font-semibold text-fg">
              Available Times ({selectedSlots.length} selected) *
            </label>
            <div className="space-y-2">
              {poll.slots.map((slot) => {
                const isSelected = selectedSlots.includes(slot);
                const tally = tallies.find((t) => t.slot === slot);
                const voteCount = tally?.votesCount ?? 0;
                const voters = tally?.voters ?? [];
                const percentage = totalVoters > 0 ? Math.round((voteCount / totalVoters) * 100) : 0;

                return (
                  <button
                    key={slot}
                    type="button"
                    onClick={() => toggleSlot(slot)}
                    className={`w-full text-left rounded-xl p-3 border transition-all flex items-center justify-between gap-3 ${
                      isSelected
                        ? 'border-accent bg-accent-soft ring-1 ring-accent'
                        : 'border-border bg-surface hover:border-accent'
                    }`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => {}} // Handled by button click
                          className="h-4 w-4 rounded accent-accent"
                        />
                        <span className="font-semibold text-xs text-fg">
                          {formatSlotTime(slot)}
                        </span>
                      </div>

                      {voters.length > 0 && (
                        <p className="mt-1 text-[0.6875rem] text-muted pl-6 truncate">
                          {voters.join(', ')}
                        </p>
                      )}
                    </div>

                    <div className="text-right shrink-0">
                      <span className="text-xs font-bold text-fg">{voteCount}</span>
                      <span className="text-[0.625rem] text-muted block">
                        {percentage}%
                      </span>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || selectedSlots.length === 0}
            className="tap-target w-full rounded-full bg-accent py-2.5 text-xs font-semibold text-accent-fg hover:opacity-90 disabled:opacity-50"
          >
            {submitting ? 'Submitting…' : 'Submit Availability'}
          </button>
        </form>
      )}

      {/* Public Voter Heatmap Overview */}
      <section className="rounded-2xl border border-border bg-surface-2 p-5 sm:p-6 space-y-3">
        <h2 className="text-sm font-semibold text-fg">Group Availability Overview</h2>
        <div className="space-y-2">
          {poll.slots.map((slot) => {
            const tally = tallies.find((t) => t.slot === slot);
            const voteCount = tally?.votesCount ?? 0;
            const voters = tally?.voters ?? [];
            const percentage = totalVoters > 0 ? Math.round((voteCount / totalVoters) * 100) : 0;
            const isWinner = poll.finalized_slot === slot;

            return (
              <div key={slot} className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="font-medium text-fg">
                    {formatSlotTime(slot)} {isWinner && '🏆'}
                  </span>
                  <span className="text-muted">
                    {voteCount} of {totalVoters} ({percentage}%)
                  </span>
                </div>
                <div className="h-2 w-full rounded-full bg-surface-3 overflow-hidden">
                  <div
                    className={`h-full rounded-full ${isWinner ? 'bg-success' : 'bg-accent'}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
                {voters.length > 0 && (
                  <p className="text-[0.625rem] text-muted truncate">{voters.join(', ')}</p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      <LegalFooter />
    </div>
  );
}
